import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { GUIDED_TIER, PRICING, TIERS } from "@/data/site";
import { ButtonLink, Eyebrow, FadeIn } from "@/components/blocks";

export default function PricingTable({ showHeading = true }) {
  const [cur, setCur] = useState("INR");
  const prices = PRICING[cur];

  return (
    <section data-testid="pricing-section" className="bg-bone px-6 py-28 text-ink sm:px-10 sm:py-36 lg:px-16">
      <div className="mx-auto max-w-[1440px]">
        <div className={`flex ${showHeading ? "flex-col justify-between gap-10 lg:flex-row lg:items-end" : "justify-end"}`}>
          {showHeading && (
            <div>
              <FadeIn>
                <Eyebrow tone="ink">Coaching Tiers</Eyebrow>
              </FadeIn>
              <FadeIn delay={0.1}>
                <h2 className="mt-8 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
                  Real coaching. <span className="text-ink/40">Real oversight.</span>
                </h2>
              </FadeIn>
              <FadeIn delay={0.15}>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-smoke sm:text-lg">
                  Live 1:1 video coaching, or a guided track you run on your own schedule — every
                  plan reviewed by an expert coach.
                </p>
              </FadeIn>
            </div>
          )}
          <FadeIn delay={0.2}>
            <div className="flex border border-ink/20" role="group" aria-label="Currency">
              {["INR", "USD"].map((c) => (
                <button
                  key={c}
                  data-testid={`pricing-currency-toggle-${c.toLowerCase()}`}
                  onClick={() => setCur(c)}
                  aria-pressed={cur === c}
                  className={`px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    cur === c ? "bg-ink text-lime" : "text-ink/60 hover:text-ink"
                  }`}
                >
                  {PRICING[c].label} ({PRICING[c].symbol})
                </button>
              ))}
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.05}>
          <div
            data-testid="guided-tier-card"
            className="mt-16 grid gap-10 border border-white/10 bg-ink p-8 text-white sm:p-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16"
          >
            <div>
              <p className="inline-flex items-center gap-2 border border-lime/30 bg-lime/10 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-lime">
                {GUIDED_TIER.badge}
              </p>
              <h3 className="mt-6 font-display text-3xl font-black uppercase tracking-tight sm:text-4xl">
                {GUIDED_TIER.name}
              </h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-ash sm:text-base">
                {GUIDED_TIER.blurb}
              </p>
              <p className="mt-8 font-display text-5xl font-black tracking-tight sm:text-6xl">
                {prices.guided}
                <span className="ml-2 align-middle font-mono text-[11px] font-normal uppercase tracking-[0.2em] text-ash">
                  / month
                </span>
              </p>
              <p
                data-testid="guided-student-pill"
                className="mt-5 inline-flex border border-lime/25 bg-lime/5 px-3.5 py-2 font-mono text-[10px] uppercase leading-relaxed tracking-[0.16em] text-lime/90"
              >
                Student Concession Available — 25% off with valid student ID ({GUIDED_TIER.student[cur]}/mo)
              </p>
              <div className="mt-10">
                <ButtonLink to="/start?track=guided" variant="lime" testid="guided-cta-apply">
                  {GUIDED_TIER.cta}
                </ButtonLink>
                <div className="mt-5">
                  <Link
                    to="/start?track=guided&concession=student"
                    data-testid="guided-student-link"
                    className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-ash transition-colors duration-300 hover:text-lime"
                  >
                    Are you a student? Apply for Student Concession
                    <ArrowRight className="h-3.5 w-3.5 text-lime transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
            <ul className="space-y-4 border-t border-white/10 pt-8 lg:border-l lg:border-t-0 lg:pl-16 lg:pt-0">
              {GUIDED_TIER.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm leading-relaxed text-bone/85 sm:text-base">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-lime" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </FadeIn>

        <div className="mt-8 grid gap-px border border-ink/15 bg-ink/15 lg:grid-cols-2">
          {TIERS.map((t, i) => (
            <FadeIn
              key={t.key}
              delay={i * 0.1}
              className={`relative flex flex-col p-8 sm:p-12 ${t.featured ? "bg-ink text-bone" : "bg-bone"}`}
            >
              {t.badge && (
                <p
                  data-testid="pricing-tier-badge"
                  className="absolute right-8 top-8 border border-lime/40 bg-lime/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-lime sm:right-12 sm:top-12"
                >
                  {t.badge}
                </p>
              )}
              <p className={`font-mono text-[10px] uppercase tracking-[0.3em] ${t.featured ? "text-lime" : "text-ink/50"}`}>
                Tier {String(i + 1).padStart(2, "0")}
              </p>
              <h3 data-testid={`pricing-tier-${t.key}`} className="mt-4 font-display text-3xl font-black uppercase tracking-tight sm:text-4xl">
                {t.name}
                <span className={`ml-3 align-middle font-mono text-[11px] font-normal uppercase tracking-[0.2em] ${t.featured ? "text-ash" : "text-ink/50"}`}>
                  {t.suffix}
                </span>
              </h3>
              <p className={`mt-4 max-w-md text-sm leading-relaxed sm:text-base ${t.featured ? "text-ash" : "text-smoke"}`}>
                {t.blurb}
              </p>
              <div className="mt-10">
                <p className="font-display text-5xl font-black tracking-tight sm:text-6xl">
                  {prices[t.key]}
                  <span className={`ml-2 align-middle font-mono text-[11px] font-normal uppercase tracking-[0.2em] ${t.featured ? "text-ash" : "text-smoke"}`}>
                    / month
                  </span>
                </p>
              </div>
              <ul className={`mt-10 flex-1 space-y-4 border-t pt-10 ${t.featured ? "border-bone/15" : "border-ink/15"}`}>
                {t.features.map((f) => (
                  <li key={f} className={`flex items-start gap-3 text-sm sm:text-base ${t.featured ? "text-bone/85" : "text-ink/80"}`}>
                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-lime" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-12">
                <ButtonLink
                  to="/start"
                  testid={`pricing-cta-${t.key}`}
                  variant={t.featured ? "lime" : "outlineInk"}
                  className="w-full justify-center"
                >
                  {t.cta}
                </ButtonLink>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.15}>
          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-ink/50">
            Every engagement begins with a movement assessment — your coach and pathway are confirmed from there.
          </p>
        </FadeIn>
      </div>
    </section>
  );
}
